import JSZip from "jszip";
import { loadPdfJs } from "../Followup/annualMaskClinicalPdfReader";
import { containsBookingOrder, extractBookingAhi, getAhiReportCandidates } from "./BookingAhiRefillUtils";

export const withAhiTimeout = async (promise, label, milliseconds = 60000) => {
  let timer;
  try {
    return await Promise.race([
      promise,
      new Promise((resolve, reject) => { timer = setTimeout(() => reject(new Error(`${label} timed out.`)), milliseconds); }),
    ]);
  } finally { clearTimeout(timer); }
};

export const readAhiReportText = async (buffer, filename) => {
  if (/\.docx$/i.test(filename)) {
    const zip = await JSZip.loadAsync(buffer);
    const documentFile = zip.file("word/document.xml");
    if (!documentFile) throw new Error("Invalid DOCX report.");
    const xml = new DOMParser().parseFromString(await documentFile.async("string"), "application/xml");
    if (xml.getElementsByTagName("parsererror").length) throw new Error("Unreadable DOCX report.");
    return Array.from(xml.getElementsByTagNameNS("*", "p")).map((paragraph) =>
      Array.from(paragraph.getElementsByTagNameNS("*", "t")).map((node) => node.textContent).join("")
    ).join("\n");
  }
  if (!/\.pdf$/i.test(filename)) throw new Error("Unsupported report format; PDF or DOCX required.");
  const pdfjs = await withAhiTimeout(loadPdfJs(), "PDF parser loading");
  const task = pdfjs.getDocument({ data: buffer });
  try {
    const pdf = await withAhiTimeout(task.promise, "PDF opening");
    const pages = [];
    for (let number = 1; number <= pdf.numPages; number += 1) {
      const page = await withAhiTimeout(pdf.getPage(number), "PDF page loading");
      const content = await withAhiTimeout(page.getTextContent(), "PDF text reading");
      // Keep line boundaries so table columns and surrounding labels are not
      // silently treated as a single AHI. Scans are logged for manual review.
      let previousY;
      let line = "";
      const lines = [];
      content.items.forEach((item) => {
        const y = item.transform && item.transform[5];
        if (previousY !== undefined && y !== undefined && Math.abs(y - previousY) > 3) {
          lines.push(line); line = "";
        }
        line += `${item.str || ""} `;
        previousY = y;
        if (item.hasEOL) { lines.push(line); line = ""; previousY = undefined; }
      });
      lines.push(line);
      pages.push(lines.join("\n"));
      page.cleanup();
    }
    return pages.join("\n");
  } finally { await task.destroy(); }
};

export const readBookingReportAhi = async ({ item, reports, getSignedUrl }) => {
  const files = getAhiReportCandidates(reports, item.data, item.ref.path);
  if (!files.length) throw new Error("No Sleep Study Report found for this booking.");
  const values = [];
  const problems = [];
  for (const file of files) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 60000);
    try {
      if (!/\.(pdf|docx)$/i.test(file.fileName)) throw new Error("Unsupported report format; PDF or DOCX required.");
      const signed = await withAhiTimeout(getSignedUrl({ folder: "reports", hn: file.hn, id: file.id, filename: file.fileName }), "Report URL request");
      const response = await fetch(signed.data, { signal: controller.signal });
      if (!response.ok) throw new Error(`Report download failed (HTTP ${response.status}).`);
      const buffer = await response.arrayBuffer();
      clearTimeout(timer);
      const reportText = await withAhiTimeout(readAhiReportText(buffer, file.fileName), "Report reading", 120000);
      if (!file.matched && !containsBookingOrder(reportText, (item.data.extendedProps || {}).order)) {
        throw new Error("Cannot match report to this booking's order.");
      }
      values.push(extractBookingAhi(reportText));
    } catch (error) {
      problems.push(`${file.fileName}: ${(error && error.message) || error}`);
    } finally { clearTimeout(timer); }
  }
  // Do not choose a convenient readable file when another candidate cannot be
  // checked; it may be a revision with a different AHI.
  if (problems.length) throw new Error(problems.join(" | "));
  if (new Set(values).size !== 1) throw new Error(`Reports disagree on AHI: ${values.join(", ")}.`);
  return values[0];
};
